import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Lock, Send } from "lucide-react";
import { API, formatApiError } from "@/lib/api";

const ENTITIES = [
  ["sole_prop", "Sole proprietor / individual"],
  ["single_llc", "Single-member LLC"],
  ["llc", "LLC"],
  ["s_corp", "S corporation"],
  ["c_corp", "C corporation"],
  ["partnership", "Partnership"],
  ["nonprofit", "Nonprofit / other"],
];

const fmtTin = (raw, type) => {
  const d = (raw || "").replace(/\D/g, "").slice(0, 9);
  if (type === "ein") return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d;
  if (d.length > 5) return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
  if (d.length > 3) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return d;
};

const input = "w-full px-3 py-2 border-2 border-ink font-mono text-sm";
const label = "overline mb-2 text-[10px] block";

export default function PublicW9() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    first_name: "", last_name: "", business_name: "", tin_type: "ssn",
    entity_type: "sole_prop", tin: "", email: "", phone: "", address: "",
    city: "", state: "", zip_code: "", backup_withholding_exempt: false,
    certified: false, signature_name: "", e_delivery_consent: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/w9/${token}`);
        setCtx(r.data);
        setF((p) => ({ ...p, email: r.data.email || "" }));
      } catch (e) { setError(formatApiError(e)); }
    })();
  }, [token]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/w9/${token}`, {
        ...f, tin: (f.tin || "").replace(/\D/g, ""),
      });
      setDone(r.data.message);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="hard-card p-8 max-w-lg font-mono text-sm" data-testid="w9-error">{error}</div>
    </div>
  );
  if (!ctx) return <div className="min-h-screen flex items-center justify-center font-mono text-sm">LOADING…</div>;

  if (done) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="hard-card p-8 max-w-lg" data-testid="w9-done">
        <ShieldCheck className="w-6 h-6 mb-3" />
        <div className="font-heading font-black text-2xl tracking-tight">W-9 received</div>
        <div className="font-mono text-sm mt-2">{done}</div>
      </div>
    </div>
  );

  const isBiz = f.tin_type === "ein";

  return (
    <div className="min-h-screen py-10 px-4 flex justify-center">
      <div className="hard-card p-6 md:p-8 w-full max-w-3xl" data-testid="w9-form">
        <div className="overline mb-2">§ FORM W-9 · REQUEST FOR TAXPAYER IDENTIFICATION</div>
        <div className="font-heading font-black text-3xl md:text-4xl tracking-tight">
          {ctx.payer_name}
        </div>
        <div className="font-mono text-xs text-muted-foreground mt-2 max-w-2xl">
          {ctx.payer_name} needs your taxpayer details before paying you as a contractor. Your
          tax identification number is encrypted the moment you submit it and is never shown
          back to you in full.
        </div>
        {ctx.note ? (
          <div className="mt-3 border-2 border-ink p-3 font-mono text-xs" data-testid="w9-note">{ctx.note}</div>
        ) : null}

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div>
            <span className={label}>Legal first name</span>
            <input data-testid="w9-first-name" className={input} value={f.first_name} onChange={set("first_name")} />
          </div>
          <div>
            <span className={label}>Legal last name</span>
            <input data-testid="w9-last-name" className={input} value={f.last_name} onChange={set("last_name")} />
          </div>
          <div>
            <span className={label}>Federal tax classification</span>
            <select data-testid="w9-entity-type" className={input} value={f.entity_type} onChange={set("entity_type")}>
              {ENTITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <span className={label}>Tax ID type</span>
            <select data-testid="w9-tin-type" className={input} value={f.tin_type}
              onChange={(e) => setF({ ...f, tin_type: e.target.value, tin: "" })}>
              <option value="ssn">Personal SSN or ITIN</option>
              <option value="ein">Business EIN</option>
            </select>
          </div>
          {isBiz ? (
            <div className="md:col-span-2">
              <span className={label}>Legal business name (as registered with the IRS)</span>
              <input data-testid="w9-business-name" className={input} value={f.business_name} onChange={set("business_name")} />
            </div>
          ) : null}
          <div>
            <span className={label}>{isBiz ? "EIN" : "SSN or ITIN"}</span>
            <input data-testid="w9-tin" className={input} inputMode="numeric"
              placeholder={isBiz ? "XX-XXXXXXX" : "XXX-XX-XXXX"}
              value={fmtTin(f.tin, f.tin_type)}
              onChange={(e) => setF({ ...f, tin: e.target.value.replace(/\D/g, "").slice(0, 9) })} />
          </div>
          <div>
            <span className={label}>Email</span>
            <input data-testid="w9-email" className={input} value={f.email} onChange={set("email")} />
          </div>
          <div>
            <span className={label}>Phone</span>
            <input data-testid="w9-phone" className={input} value={f.phone} onChange={set("phone")} />
          </div>
          <div>
            <span className={label}>Street address</span>
            <input data-testid="w9-address" className={input} value={f.address} onChange={set("address")} />
          </div>
          <div>
            <span className={label}>City</span>
            <input data-testid="w9-city" className={input} value={f.city} onChange={set("city")} />
          </div>
          <div>
            <span className={label}>State</span>
            <input data-testid="w9-state" className={input} maxLength={2}
              value={f.state} onChange={(e) => setF({ ...f, state: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <span className={label}>ZIP code</span>
            <input data-testid="w9-zip" className={input} value={f.zip_code} onChange={set("zip_code")} />
          </div>
        </div>

        <div className="mt-6 border-2 border-ink p-4 font-mono text-xs space-y-3">
          <label className="flex gap-3 items-start cursor-pointer">
            <input type="checkbox" data-testid="w9-exempt" checked={f.backup_withholding_exempt}
              onChange={(e) => setF({ ...f, backup_withholding_exempt: e.target.checked })} />
            <span>I am exempt from backup withholding.</span>
          </label>
          <label className="flex gap-3 items-start cursor-pointer">
            <input type="checkbox" data-testid="w9-consent" checked={f.e_delivery_consent}
              onChange={(e) => setF({ ...f, e_delivery_consent: e.target.checked })} />
            <span>I consent to receive my Form 1099-NEC electronically instead of on paper. I
              can withdraw this consent by contacting the payer, and I will then receive a paper
              copy.</span>
          </label>
          <label className="flex gap-3 items-start cursor-pointer">
            <input type="checkbox" data-testid="w9-certify" checked={f.certified}
              onChange={(e) => setF({ ...f, certified: e.target.checked })} />
            <span>Under penalties of perjury, I certify that the number shown above is my
              correct taxpayer identification number, and that the information on this form is
              true and complete.</span>
          </label>
          <div>
            <span className={label}>Electronic signature — type your full legal name</span>
            <input data-testid="w9-signature" className={input} value={f.signature_name} onChange={set("signature_name")} />
          </div>
        </div>

        <button onClick={submit} disabled={busy || !f.certified} data-testid="w9-submit"
          className="hard-btn mt-5 py-3 px-6 font-mono uppercase text-[11px] tracking-widest inline-flex items-center gap-2 disabled:opacity-40">
          <Send className="w-3 h-3" /> {busy ? "Submitting…" : "Submit my W-9"}
        </button>
        <div className="mt-4 font-mono text-[10px] opacity-70 flex gap-2">
          <Lock className="w-3 h-3 shrink-0 mt-[2px]" />
          This link is unique to you and expires. Checkmate Payroll is not a tax adviser and does
          not file on your behalf without your instruction.
        </div>
      </div>
    </div>
  );
}
